import { z } from 'zod';
/**
 * Contrats de l'ORGANISATION physique des médias (LOT 2 — déplacement de fichiers).
 * Source unique de vérité front + back.
 *
 * - `sanitizeDestFolder` : normalise/valide un dossier de destination RELATIF (préfixe
 *   de `rel_path` sous la racine bibliothèque). Fonction PURE, réutilisée à
 *   l'identique côté web (désactivation du bouton « Déplacer » si invalide) et côté
 *   API (validation avant écriture disque). Rejette toute traversée / chemin absolu.
 * - `moveRequestSchema`  : corps de `POST /api/libraries/:id/move`.
 * - `moveResultSchema`   : ventilation d'un job `move` (comptage + audit).
 *
 * SÉCURITÉ : ce module ne fait QUE valider une chaîne. La garde anti-traversée
 * DÉFINITIVE reste le `safeJoin(root, rel)` côté job (résolution absolue bornée à
 * la racine). `sanitizeDestFolder` est une première barrière (rejet syntaxique)
 * ET la normalisation du préfixe POSIX.
 */
// ---------------------------------------------------------------------------
// Dossier de destination (préfixe relatif validé)
// ---------------------------------------------------------------------------
/** Un segment de chemin est interdit s'il est vide, `.`, `..` ou porte un `\0`… */
function isForbiddenSegment(segment) {
    if (segment === '' || segment === '.' || segment === '..')
        return true;
    // Caractères de contrôle (0x00–0x1F) : interdits dans un segment de chemin.
    // (contrôle-uniquement ; on ne restreint pas les autres caractères pour rester
    // permissif sur les noms de dossiers légitimes.)
    for (let i = 0; i < segment.length; i += 1) {
        const code = segment.charCodeAt(i);
        if (code <= 0x1f)
            return true;
    }
    return false;
}
/**
 * Détecte un chemin ABSOLU (POSIX ou Windows) que l'on refuse comme destination
 * relative : `/…`, `\…`, ou lettre de lecteur `C:` / `C:\` / `C:/`.
 */
function isAbsoluteLike(input) {
    if (input.startsWith('/') || input.startsWith('\\'))
        return true;
    // Lettre de lecteur Windows : `C:` éventuellement suivi de `/` ou `\`.
    if (/^[a-zA-Z]:/.test(input))
        return true;
    return false;
}
/**
 * Normalise et valide un dossier de destination RELATIF sous la racine bibliothèque.
 *
 * Règles (→ `null` si l'une échoue) :
 *  - `trim` puis découpe sur `/` ET `\` (tolère une saisie mixte).
 *  - refus si l'entrée trimée est vide.
 *  - refus si chemin absolu (`/`, `\`, lettre de lecteur `C:`) — testé AVANT de
 *    retirer les séparateurs de tête/queue.
 *  - un slash de tête/queue est TOLÉRÉ (retiré) ; un slash INTERNE dupliqué
 *    (`a//b`) reste un segment vide → refus.
 *  - refus si un segment est vide / `.` / `..` / contient un caractère de contrôle.
 *
 * Succès → préfixe POSIX normalisé : segments joints par `/`, SANS slash de tête
 * ni de queue (ex. ` with_sound/ ` → `with_sound`, `a\b` → `a/b`).
 */
export function sanitizeDestFolder(input) {
    const trimmed = input.trim();
    if (trimmed === '')
        return null;
    if (isAbsoluteLike(trimmed))
        return null;
    // Retire les séparateurs de tête/queue (un slash de queue est bénin) SANS toucher
    // aux séparateurs internes : `a//b` conserve donc un segment vide → rejeté plus bas.
    const stripped = trimmed.replace(/^[/\\]+/, '').replace(/[/\\]+$/, '');
    if (stripped === '')
        return null;
    // Découpe sur les DEUX séparateurs (une saisie Windows `a\b` doit produire `a/b`).
    const segments = stripped.split(/[/\\]/);
    for (const segment of segments) {
        if (isForbiddenSegment(segment))
            return null;
    }
    return segments.join('/');
}
// ---------------------------------------------------------------------------
// Liste de TOUS les dossiers (GET /api/libraries/:id/all-folders) — LOT 5
// ---------------------------------------------------------------------------
/**
 * Une entrée de dossier de la bibliothèque : son chemin RELATIF (préfixe POSIX de
 * `rel_path`, `''` pour la racine) et le nombre d'assets présents qui y résident
 * DIRECTEMENT (fichiers enfants immédiats, hors sous-dossiers).
 *
 * NB : la liste ne remonte que les dossiers qui CONTIENNENT directement des fichiers.
 * Les dossiers ANCESTRAUX intermédiaires (ex. `2024` quand seuls des fichiers
 * `2024/vacances/…` existent) sont recomposés côté client (helper `folder-tree`) →
 * ils restent sélectionnables comme destination même sans `assetCount` propre.
 */
export const folderEntrySchema = z.object({
    path: z.string(),
    assetCount: z.number().int().nonnegative(),
});
/** Réponse de `GET /api/libraries/:id/all-folders` : tous les dossiers à fichiers. */
export const folderListResultSchema = z.object({
    folders: z.array(folderEntrySchema),
});
// ---------------------------------------------------------------------------
// Requête de déplacement (POST /api/libraries/:id/move)
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/move`. Sélecteur de cible :
 *  - `assetIds` fourni (non vide) → ces assets précis sont déplacés.
 *  - sinon `hasAudio === true` → tous les médias PRÉSENTS avec `has_audio = true`.
 *
 * Au moins l'un des deux doit sélectionner quelque chose (validé côté route :
 * `assetIds` non vide OU `hasAudio === true`).
 *
 * `destFolder` est validé finement (traversée/absolu) par `sanitizeDestFolder` au
 * niveau route et job ; ici on n'exige qu'une chaîne non vide (le message d'erreur
 * précis relève de la couche route).
 */
export const moveRequestSchema = z.object({
    destFolder: z.string().min(1),
    dryRun: z.boolean().optional().default(false),
    assetIds: z.array(z.uuid()).optional(),
    hasAudio: z.boolean().optional(),
});
/**
 * Ventilation d'un job `move`. Chaque asset ciblé tombe dans exactement une
 * catégorie (sauf `total`, qui est le nombre d'assets ciblés) :
 *  - `moved`     : fichier déplacé (ou, en dry-run, qui SERAIT déplacé).
 *  - `skipped`   : déjà rangé sous le dossier de destination (aucune action).
 *  - `conflicts` : un fichier existe DÉJÀ à la destination → non écrasé.
 *  - `failed`    : chemin refusé (traversée) ou erreur d'E/S disque.
 */
export const moveResultSchema = z.object({
    total: z.number().int().nonnegative(),
    moved: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Téléversement (POST /api/libraries/:id/upload) — XPHOTO-16
// ---------------------------------------------------------------------------
/**
 * Un fichier IGNORÉ au téléversement, avec le nom d'origine et la raison (code
 * stable, lisible par la SPA pour un message localisé) :
 *  - `non_media`     : extension hors de l'allow-list des médias (jamais écrit).
 *  - `bad_name`      : nom de fichier vide/dangereux (aucun basename sûr dérivable).
 *  - `empty`         : partie de fichier vide (0 octet).
 *  - `write_error`   : échec d'écriture disque (rare — la route gate déjà l'écriture).
 *  - `convert_failed`: HEIC/HEIF illisible ou conversion vers WebP échouée (décodage,
 *    réencodage ou recopie EXIF) → rien n'entre dans la bibliothèque. L'original HEIC
 *    n'étant PAS conservé, un EXIF non recopié serait une perte irréversible : on
 *    refuse l'import plutôt que d'indexer une photo amputée de sa date de prise de vue.
 *  - `too_large`     : image hors des limites du conteneur WebP (côté > 16383 px, cas
 *    des panoramas) → non convertible, jamais tronquée en silence.
 */
export const uploadSkipReasonSchema = z.enum([
    'non_media',
    'bad_name',
    'empty',
    'write_error',
    'convert_failed',
    'too_large',
]);
export const uploadSkippedFileSchema = z.object({
    filename: z.string(),
    reason: uploadSkipReasonSchema,
});
/**
 * Résultat de `POST /api/libraries/:id/upload` (multipart/form-data). Une requête
 * multipart n'a PAS de schéma de CORPS Zod (le `destFolder` et les parties de
 * fichier sont validés manuellement côté route) : ce schéma décrit la RÉPONSE.
 *
 *  - `uploaded`   : nombre de fichiers écrits sur disque (nom final éventuellement
 *    dédupliqué en cas de collision — jamais d'écrasement).
 *  - `skipped`    : fichiers refusés (voir `uploadSkipReasonSchema`).
 *  - `destFolder` : dossier de destination NORMALISÉ (préfixe POSIX ; `''` = racine).
 *  - `jobId`      : id du job `scan` enfilé pour indexer les fichiers écrits (présent
 *    dès qu'au moins un fichier a été écrit → suivi dans la cloche de notifications).
 */
export const uploadResultSchema = z.object({
    uploaded: z.number().int().nonnegative(),
    skipped: z.array(uploadSkippedFileSchema),
    destFolder: z.string(),
    jobId: z.uuid().optional(),
});
/**
 * Normalise et valide un NOM DE FICHIER téléversé en un BASENAME sûr (aucune
 * composante de chemin). PURE, réutilisable côté web (aperçu) et API (écriture).
 *
 * Règles (→ `null` si l'entrée ne donne aucun basename sûr) :
 *  - découpe sur `/` ET `\` puis ne garde que le DERNIER segment (retire tout
 *    répertoire, y compris une traversée `../` glissée dans le nom du fichier) ;
 *  - `trim` ; refus si vide, `.`, `..`, ou porteur d'un caractère de contrôle
 *    (0x00–0x1F) ou d'un caractère de chemin résiduel (`/`, `\`).
 *
 * NB : cette fonction ne vérifie PAS l'extension (média/non-média) — c'est le rôle
 * de l'allow-list `MEDIA_EXTENSIONS` du scanner, côté route.
 */
export function sanitizeUploadFilename(input) {
    // Ne conserver que le dernier segment (retire répertoires + traversée).
    const segments = input.split(/[/\\]/);
    const base = (segments[segments.length - 1] ?? '').trim();
    if (base === '' || base === '.' || base === '..')
        return null;
    for (let i = 0; i < base.length; i += 1) {
        const code = base.charCodeAt(i);
        if (code <= 0x1f)
            return null;
    }
    // Garde-fou : plus aucun séparateur de chemin ne doit subsister.
    if (base.includes('/') || base.includes('\\'))
        return null;
    return base;
}
// ---------------------------------------------------------------------------
// Correction des extensions de fichiers (« un vrai JPEG nommé .png »)
// ---------------------------------------------------------------------------
/**
 * Un renommage prévu : `from` (chemin actuel) → `to` (chemin corrigé, extension
 * canonique du type RÉEL). POSIX, relatif à la racine. Le `to` d'aperçu est NAÏF
 * (sans déduplication de collision, faite au moment du renommage réel).
 */
export const plannedRenameSchema = z.object({
    from: z.string(),
    to: z.string(),
});
/** Longueur max de l'échantillon de renommages renvoyé (garde-fou UI). */
export const EXTENSION_SCAN_SAMPLE_MAX = 100;
/**
 * Query de `POST /api/libraries/:id/extensions/scan` (LECTURE : compte les fichiers
 * mal étiquetés sans rien renommer). `folder` = sous-arbre optionnel.
 */
export const extensionsScanRequestSchema = z.object({
    folder: z.string().min(1).optional(),
});
/**
 * Résultat d'un scan : nombre TOTAL de fichiers dont l'extension ne correspond pas
 * au contenu réel, + un échantillon borné (`from → to`) pour l'affichage.
 */
export const extensionsScanResultSchema = z.object({
    total: z.number().int().nonnegative(),
    sample: z.array(plannedRenameSchema),
});
/**
 * Corps de `POST /api/libraries/:id/extensions/fix` : enfile un job `rename_extensions`
 * qui RENOMME les fichiers mal étiquetés vers leur extension canonique. `folder` =
 * sous-arbre optionnel. `dryRun` = compte sans renommer.
 */
export const fixExtensionsRequestSchema = z.object({
    folder: z.string().min(1).optional(),
    dryRun: z.boolean().default(false),
});
/**
 * Ventilation d'un job `rename_extensions` : total ciblé / renommés / renommés avec un
 * suffixe de déduplication (collision) / échecs.
 */
export const fixExtensionsResultSchema = z.object({
    total: z.number().int().nonnegative(),
    renamed: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=organize.js.map